import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import FileUpload from "./FileUpload";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blankLine = { item_id: "", qty: 1, rate: "" };

const toForm = (purchase) => ({
  bill_number: purchase?.bill_number || purchase?.reference || "",
  po_number: purchase?.po_number || "",
  supplier: purchase?.supplier || "",
  bill_date: purchase?.bill_date || purchase?.purchase_date || "",
  remarks: purchase?.remarks || "",
  attachment_url: purchase?.attachment_url || null,
  edit_reason: "",
  lines: purchase?.lines?.map((line) => ({
    item_id: line.item_id,
    qty: line.qty,
    rate: line.rate ?? "",
  })) || [blankLine],
});

export default function LinenPurchaseEditDialog({
  open,
  onOpenChange,
  purchase,
  items,
  onSaved,
}) {
  const [form, setForm] = useState(toForm(purchase));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(toForm(purchase));
    }
  }, [open, purchase]);

  const total = useMemo(
    () =>
      form.lines.reduce(
        (sum, line) => sum + Number(line.qty || 0) * Number(line.rate || 0),
        0,
      ),
    [form.lines],
  );

  const updateLine = (index, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    }));
  };

  const save = async () => {
    const lines = form.lines.filter(
      (line) => line.item_id && Number(line.qty) > 0,
    );
    if (
      !form.bill_number.trim() ||
      !form.bill_date ||
      !lines.length ||
      !form.edit_reason.trim()
    ) {
      toast.error("Bill number, date, an item, and edit reason are required");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.put(`/purchases/${purchase.id}`, {
        bill_number: form.bill_number.trim(),
        po_number: form.po_number.trim() || null,
        supplier: form.supplier.trim() || null,
        purchase_date: form.bill_date,
        bill_date: form.bill_date,
        receiving_location: "STORE_CLEAN",
        remarks: form.remarks.trim() || null,
        attachment_url: form.attachment_url,
        edit_reason: form.edit_reason.trim(),
        lines: lines.map((line) => ({
          item_id: line.item_id,
          qty: Number(line.qty),
          rate: line.rate === "" ? null : Number(line.rate),
        })),
      });
      toast.success("Purchase corrected and audit log recorded");
      onSaved(data);
      onOpenChange(false);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="linen-purchase-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="linen-purchase-edit-title">
            Edit Linen Purchase
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Bill / Invoice Number</Label>
            <Input
              value={form.bill_number}
              onChange={(event) =>
                setForm({ ...form, bill_number: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="linen-edit-bill-number"
            />
          </div>
          <div>
            <Label>Reference / PO Number</Label>
            <Input
              value={form.po_number}
              onChange={(event) =>
                setForm({ ...form, po_number: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="linen-edit-po-number"
            />
          </div>
          <div>
            <Label>Supplier</Label>
            <Input
              value={form.supplier}
              onChange={(event) =>
                setForm({ ...form, supplier: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="linen-edit-supplier"
            />
          </div>
          <div>
            <Label>Bill Date</Label>
            <Input
              type="date"
              value={form.bill_date}
              onChange={(event) =>
                setForm({ ...form, bill_date: event.target.value })
              }
              className="h-11 mt-1"
              data-testid="linen-edit-bill-date"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">
            Items received into Store Clean
          </div>
          {form.lines.map((line, index) => (
            <div
              key={`linen-edit-line-${index}`}
              className="grid grid-cols-12 gap-2 items-end"
            >
              <div className="col-span-5">
                <Label className="text-xs">Item</Label>
                <Select
                  value={line.item_id}
                  onValueChange={(value) => updateLine(index, "item_id", value)}
                >
                  <SelectTrigger
                    className="h-11 mt-1"
                    data-testid={`linen-edit-item-${index}`}
                  >
                    <SelectValue placeholder="Item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={line.qty}
                  onChange={(event) =>
                    updateLine(index, "qty", event.target.value)
                  }
                  className="h-11 mt-1"
                  data-testid={`linen-edit-qty-${index}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Unit Price</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.rate}
                  onChange={(event) =>
                    updateLine(index, "rate", event.target.value)
                  }
                  className="h-11 mt-1"
                  data-testid={`linen-edit-rate-${index}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Line Total</Label>
                <div className="h-11 mt-1 flex items-center px-3 border rounded-md bg-slate-50">
                  {Number(line.qty || 0) * Number(line.rate || 0)}
                </div>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() =>
                  setForm({
                    ...form,
                    lines: form.lines.filter(
                      (_, lineIndex) => lineIndex !== index,
                    ),
                  })
                }
                disabled={form.lines.length === 1}
                data-testid={`linen-edit-remove-line-${index}`}
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setForm({ ...form, lines: [...form.lines, { ...blankLine }] })
            }
            data-testid="linen-edit-add-line"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Line
          </Button>
          <div
            className="font-semibold text-sm"
            data-testid="linen-edit-total-value"
          >
            Total Bill Value: {total.toFixed(2)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Remarks</Label>
            <Textarea
              value={form.remarks}
              onChange={(event) =>
                setForm({ ...form, remarks: event.target.value })
              }
              data-testid="linen-edit-remarks"
            />
          </div>
          <div>
            <Label>Invoice Attachment</Label>
            <div className="mt-2">
              <FileUpload
                value={form.attachment_url}
                onChange={(attachment_url) =>
                  setForm({ ...form, attachment_url })
                }
                testid="linen-edit-attachment"
              />
            </div>
          </div>
        </div>
        <div>
          <Label>Edit Reason *</Label>
          <Textarea
            value={form.edit_reason}
            onChange={(event) =>
              setForm({ ...form, edit_reason: event.target.value })
            }
            data-testid="linen-edit-reason"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-slate-900 text-white hover:bg-slate-800"
          data-testid="linen-edit-save"
        >
          {saving ? "Saving…" : "Save Audited Correction"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
